// ── API Client ──────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL;

// ── Token Management ─────────────────────────────────────────────────────────

let accessToken: string | null = localStorage.getItem("accessToken");
let refreshToken: string | null = localStorage.getItem("refreshToken");

function saveTokens(access: string, refresh: string) {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem("accessToken", access);
  localStorage.setItem("refreshToken", refresh);
}

function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
}

// ── API Request Helper ───────────────────────────────────────────────────────

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    // Try refreshing the token
    if (refreshToken) {
      try {
        const refreshRes = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          saveTokens(data.accessToken, data.refreshToken);
          headers["Authorization"] = `Bearer ${accessToken}`;
          const retryRes = await fetch(`${API_BASE}${path}`, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
          });
          if (!retryRes.ok) {
            const err = await retryRes
              .json()
              .catch(() => ({ error: "Request failed" }));
            throw new Error(err.error || `HTTP ${retryRes.status}`);
          }
          return retryRes.json();
        } else {
          clearTokens();
          window.location.href = "/login";
          throw new Error("Session expired");
        }
      } catch {
        clearTokens();
        window.location.href = "/login";
        throw new Error("Session expired");
      }
    } else {
      clearTokens();
      window.location.href = "/login";
      throw new Error("Session expired");
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err.error || `Request failed`);
  }

  return res.json();
}

// ── Auth API ─────────────────────────────────────────────────────────────────

export const authApi = {
  login: async (username: string, password: string) => {
    const data: {
      user: { id: string; username: string; displayName: string; role: string };
      accessToken: string;
      refreshToken: string;
    } = await request("POST", "/api/auth/login", { username, password });
    saveTokens(data.accessToken, data.refreshToken);
    localStorage.setItem("auth.v1", JSON.stringify({ user: data.user }));
    return data.user;
  },

  refresh: async () => {
    if (!refreshToken) return null;
    const data: { accessToken: string; refreshToken: string } = await request(
      "POST",
      "/api/auth/refresh",
      { refreshToken },
    );
    saveTokens(data.accessToken, data.refreshToken);
    return data;
  },

  logout: async () => {
    try {
      await request("POST", "/api/auth/logout", { refreshToken });
    } catch {
      // Ignore errors on logout
    }
    clearTokens();
    localStorage.removeItem("auth.v1");
  },

  getMe: async () => {
    const data: {
      user: { id: string; username: string; displayName: string; role: string };
    } = await request("GET", "/api/auth/me");
    return data.user;
  },

  isAuthenticated: () => !!accessToken,
};

// ── Items API ────────────────────────────────────────────────────────────────

export type ApiItem = {
  id: string;
  sku: string;
  name: string;
  buyingPrice: number;
  sellingPrice: number;
  openStock: number;
  addedStock: number;
  stock: number;
};

export const itemsApi = {
  list: async () => {
    const data: { items: ApiItem[] } = await request("GET", "/api/items");
    return data.items;
  },

  get: async (id: string) => {
    const data: { item: ApiItem } = await request("GET", `/api/items/${id}`);
    return data.item;
  },

  create: async (item: {
    sku: string;
    name: string;
    buyingPrice: number;
    sellingPrice: number;
  }) => {
    const data: { item: ApiItem } = await request("POST", "/api/items", item);
    return data.item;
  },

  update: async (
    id: string,
    item: {
      sku?: string;
      name?: string;
      buyingPrice?: number;
      sellingPrice?: number;
    },
  ) => {
    await request("PUT", `/api/items/${id}`, item);
  },

  addStock: async (id: string, qty: number, cost: number, payment?: string) => {
    await request("POST", `/api/items/${id}/stock`, {
      qty,
      cost,
      payment: payment || "cash",
    });
  },
};

// ── Sales API ────────────────────────────────────────────────────────────────

export type ApiSale = {
  id: string;
  itemId: string;
  qty: number;
  unitPrice: number;
  total: number;
  payment: "cash" | "mpesa" | "bank";
  receiptId?: string;
  at: number;
};

export const salesApi = {
  list: async () => {
    const data: { sales: ApiSale[] } = await request("GET", "/api/sales");
    return data.sales;
  },

  getByReceipt: async (receiptId: string) => {
    const data: { sales: ApiSale[] } = await request(
      "GET",
      `/api/sales/receipt/${receiptId}`,
    );
    return data.sales;
  },

  create: async (sale: {
    itemId: string;
    qty: number;
    payment: string;
    receiptId?: string;
  }) => {
    const data: { sale: ApiSale } = await request("POST", "/api/sales", sale);
    return data.sale;
  },

  checkout: async (
    lines: { itemId: string; qty: number }[],
    payment: string,
  ) => {
    const data: { sales: ApiSale[]; receiptId: string } = await request(
      "POST",
      "/api/sales/checkout",
      {
        lines,
        payment,
      },
    );
    return data;
  },
};

// ── Cash API ─────────────────────────────────────────────────────────────────

export type ApiCashEntry = {
  id: string;
  type: string;
  description: string;
  amount: number;
  at: number;
};

export type ApiCashbook = {
  purchases: number;
  expenditure: number;
  debts: number;
  debtsPaid: number;
  previousBalance: number;
  total: number;
  banking: number;
  mpesa: number;
  short: number;
  cashHandedOver: number;
  bcf: number;
  salesTotal: number;
  cashSales: number;
  mpesaSales: number;
  bankSales: number;
};

export const cashApi = {
  list: async () => {
    const data: { cash: ApiCashEntry[] } = await request("GET", "/api/cash");
    return data.cash;
  },

  add: async (entry: { type: string; description: string; amount: number }) => {
    const data: { cash: ApiCashEntry } = await request(
      "POST",
      "/api/cash",
      entry,
    );
    return data.cash;
  },

  getCashbook: async () => {
    const data: { cashbook: ApiCashbook } = await request(
      "GET",
      "/api/cash/cashbook",
    );
    return data.cashbook;
  },

  getReport: async () => {
    const data: { report: any[] } = await request("GET", "/api/cash/report");
    return data.report;
  },

  setPreviousBalance: async (previousBalance: number) => {
    await request("PUT", "/api/cash/previous-balance", { previousBalance });
  },
};

// ── Tabs API ─────────────────────────────────────────────────────────────────

export type ApiTabItem = {
  id: number;
  itemId: string;
  name: string;
  qty: number;
  unitPrice: number;
  total: number;
  issued: boolean;
  serverName: string;
};

export type ApiTabPayment = {
  id: string;
  amount: number;
  method: string;
  at: number;
};

export type ApiTab = {
  id: string;
  customerId: string;
  customerName: string;
  waiterId: string | null;
  status: string;
  createdAt: number;
  items: ApiTabItem[];
  payments: ApiTabPayment[];
};

export const tabsApi = {
  listAll: async () => {
    const data: { tabs: ApiTab[] } = await request("GET", "/api/tabs");
    return data.tabs;
  },

  listMy: async () => {
    const data: { tabs: ApiTab[] } = await request("GET", "/api/tabs/my");
    return data.tabs;
  },

  listWaiter: async () => {
    const data: { tabs: ApiTab[] } = await request("GET", "/api/tabs/waiter");
    return data.tabs;
  },

  create: async (customerName?: string) => {
    const data: { tab: ApiTab } = await request("POST", "/api/tabs", {
      customerName,
    });
    return data.tab;
  },

  waiterCreate: async (customerName: string) => {
    const data: { tab: ApiTab } = await request(
      "POST",
      "/api/tabs/waiter-create",
      { customerName },
    );
    return data.tab;
  },

  addItem: async (
    tabId: string,
    item: { itemId: string; name: string; qty: number; unitPrice: number },
  ) => {
    await request("POST", `/api/tabs/${tabId}/items`, item);
  },

  batchAddItems: async (
    tabId: string,
    lines: { itemId: string; name: string; qty: number; unitPrice: number }[],
  ) => {
    await request("POST", `/api/tabs/${tabId}/items/batch`, { lines });
  },

  pay: async (tabId: string, amount: number, method: string) => {
    await request("POST", `/api/tabs/${tabId}/pay`, { amount, method });
  },

  close: async (tabId: string) => {
    await request("POST", `/api/tabs/${tabId}/close`, {});
  },

  issue: async (tabId: string) => {
    await request("POST", `/api/tabs/${tabId}/issue`, {});
  },

  issueItem: async (tabId: string, itemId: string) => {
    await request("POST", `/api/tabs/${tabId}/issue-item`, { itemId });
  },
};

// ── Closed Days API ──────────────────────────────────────────────────────────

export type ApiClosedDay = {
  id: number;
  date: string;
  closedAt: number;
  previousBalance: number;
  salesTotal: number;
  cashSales: number;
  mpesaSales: number;
  bankSales: number;
  purchases: number;
  expenditure: number;
  debts: number;
  debtsPaid: number;
  cashHandedOver: number;
  banking: number;
  mpesa: number;
  bcf: number;
  sales?: ApiSale[];
  cash?: ApiCashEntry[];
  items?: ApiItem[];
};

export const closedDaysApi = {
  list: async () => {
    const data: { closedDays: ApiClosedDay[] } = await request(
      "GET",
      "/api/closed-days",
    );
    return data.closedDays;
  },

  get: async (date: string) => {
    const data: { closedDay: ApiClosedDay } = await request(
      "GET",
      `/api/closed-days/${date}`,
    );
    return data.closedDay;
  },

  close: async (closedDate?: string) => {
    const data: { closedDay: ApiClosedDay } = await request(
      "POST",
      "/api/closed-days",
      { closedDate },
    );
    return data.closedDay;
  },
};

// ── Users API ────────────────────────────────────────────────────────────────

export type ApiUser = {
  id: string;
  username: string;
  displayName: string;
  role: string;
};

export const usersApi = {
  list: async () => {
    const data: { users: ApiUser[] } = await request("GET", "/api/users");
    return data.users;
  },

  create: async (user: {
    username: string;
    password: string;
    displayName: string;
    role: string;
  }) => {
    const data: { user: ApiUser } = await request("POST", "/api/users", user);
    return data.user;
  },

  updateRole: async (id: string, role: string) => {
    await request("PUT", `/api/users/${id}/role`, { role });
  },

  delete: async (id: string) => {
    await request("DELETE", `/api/users/${id}`);
  },
};

// ── Socket.io (lazy import) ──────────────────────────────────────────────────

let socket: any = null;

export async function connectSocket() {
  // Return existing socket if it's already created (connecting or connected)
  if (socket) return socket;
  try {
    const { io } = await import("socket.io-client");
    socket = io(SOCKET_URL, {
      auth: { token: accessToken },
      transports: ["websocket", "polling"],
    });
    socket.on("connect", () => console.log("Socket connected"));
    socket.on("disconnect", () => console.log("Socket disconnected"));
    return socket;
  } catch {
    console.warn("Socket.io connection failed");
    return null;
  }
}

export function getSocket() {
  return socket;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmt(amount: number): string {
  return `KSh ${amount.toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
